import { moveOutStatusLabel, moveOutStatusClass } from '../../../utils/portvilleMoveOutRequest';
import React, { useState, useEffect } from 'react';
import { MoveOutRequest } from '../../../types/moveOutRequest';
import { MoveOutService } from '../../../services/MoveOutService';

interface MoveOutRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  adminUid: string;
  onProcessed: () => void; // 승인/반려 완료 후 콜백
}

const MoveOutRequestModal: React.FC<MoveOutRequestModalProps> = ({
  isOpen,
  onClose,
  requestId,
  adminUid,
  onProcessed
}) => {
  const [request, setRequest] = useState<MoveOutRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [approveNotes, setApproveNotes] = useState('');

  useEffect(() => {
    if (isOpen && requestId) {
      loadMoveOutRequest();
    }
  }, [isOpen, requestId]);

  const loadMoveOutRequest = async () => {
    try {
      setLoading(true);
      const data = await MoveOutService.getMoveOutRequest(requestId);
      setRequest(data);
    } catch (error) {
      console.error('퇴거신청 정보 로딩 실패:', error);
      alert('퇴거신청 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!request) return;
    
    const confirmed = window.confirm('퇴거신청을 승인하시겠습니까?');
    if (!confirmed) return;

    try {
      setProcessing(true);
      await MoveOutService.approveMoveOutRequest(request.id, adminUid, approveNotes);
      alert('퇴거신청이 승인되었습니다.');
      onProcessed();
      onClose();
    } catch (error) {
      console.error('퇴거신청 승인 실패:', error);
      alert('퇴거신청 승인에 실패했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!request || !rejectNotes.trim()) {
      alert('반려 사유를 입력해주세요.');
      return;
    }
    
    const confirmed = window.confirm('퇴거신청을 반려하시겠습니까?');
    if (!confirmed) return;

    try {
      setProcessing(true);
      await MoveOutService.rejectMoveOutRequest(request.id, adminUid, rejectNotes);
      alert('퇴거신청이 반려되었습니다.');
      onProcessed();
      onClose();
    } catch (error) {
      console.error('퇴거신청 반려 실패:', error);
      alert('퇴거신청 반려에 실패했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setShowRejectForm(false);
    setRejectNotes('');
    setApproveNotes('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">퇴거신청 상세정보</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={processing}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : request ? (
            <div className="space-y-6">
              {/* 기본 정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">신청자 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">성명</p>
                    <p className="text-sm text-gray-900">{request.residentInfo.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">연락처</p>
                    <p className="text-sm text-gray-900">{request.residentInfo.phoneNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">소속</p>
                    <p className="text-sm text-gray-900">{request.residentInfo.organization}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">호실</p>
                    <p className="text-sm text-gray-900">{request.roomNumber}호</p>
                  </div>
                </div>
              </div>

              {/* 퇴거 정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">퇴거 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">퇴거 사유</p>
                    <p className="text-sm text-gray-900">
                      {request.moveOutReason === 'personnel_change' ? '인사변경' : '개인사유'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">퇴거 희망일</p>
                    <p className="text-sm text-gray-900">
                      {request.desiredMoveOutDate.toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">신청일</p>
                    <p className="text-sm text-gray-900">
                      {request.appliedAt.toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">상태</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      moveOutStatusClass(request.status)
                    }`}>
                      {moveOutStatusLabel(request.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* 첨부파일 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">첨부파일</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">신분증 사본</span>
                    {request.attachments.idCopy ? (
                      <a
                        href={request.attachments.idCopy}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        파일 보기
                      </a>
                    ) : (
                      <span className="text-gray-400 text-sm">첨부되지 않음</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">통장 사본</span>
                    {request.attachments.bankbookCopy ? (
                      <a
                        href={request.attachments.bankbookCopy}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        파일 보기
                      </a>
                    ) : (
                      <span className="text-gray-400 text-sm">첨부되지 않음</span>
                    )}
                  </div>
                  {request.moveOutReason === 'personnel_change' && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">증빙서류</span>
                      {request.attachments.proofDocument ? (
                        <a
                          href={request.attachments.proofDocument}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          파일 보기
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">첨부되지 않음</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 처리 정보 (처리된 경우에만 표시) */}
              {request.status !== 'pending' && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">처리 정보</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">처리일</p>
                      <p className="text-sm text-gray-900">
                        {request.processedAt?.toLocaleDateString('ko-KR')}
                      </p>
                    </div>
                    {request.status === 'closed' && <p className="text-sm text-blue-800">계약종료에 따라 미처리 퇴거신청이 종결되었습니다.</p>}
                    {request.notes && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          {request.status === 'approved' ? '승인 메모' : request.status === 'closed' ? '관리자 메모' : '반려 사유'}
                        </p>
                        <p className="text-sm text-gray-900 bg-white p-3 rounded border">
                          {request.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 승인/반려 처리 (대기중인 경우에만 표시) */}
              {request.status === 'pending' && (
                <div className="border-t pt-6">
                  {!showRejectForm ? (
                    <div className="space-y-4">
                      {/* 승인 메모 입력 */}
                      <div>
                        <label htmlFor="approveNotes" className="block text-sm font-medium text-gray-700 mb-2">
                          승인 메모 (선택사항)
                        </label>
                        <textarea
                          id="approveNotes"
                          rows={3}
                          value={approveNotes}
                          onChange={(e) => setApproveNotes(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="승인 관련 메모를 입력하세요..."
                        />
                      </div>
                      
                      <div className="flex space-x-4">
                        <button
                          onClick={handleApprove}
                          disabled={processing}
                          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          {processing ? '처리 중...' : '승인'}
                        </button>
                        <button
                          onClick={() => setShowRejectForm(true)}
                          disabled={processing}
                          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                        >
                          반려
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="rejectNotes" className="block text-sm font-medium text-gray-700 mb-2">
                          반려 사유 *
                        </label>
                        <textarea
                          id="rejectNotes"
                          rows={4}
                          value={rejectNotes}
                          onChange={(e) => setRejectNotes(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                          placeholder="반려 사유를 상세히 입력해주세요..."
                          required
                        />
                      </div>
                      
                      <div className="flex space-x-4">
                        <button
                          onClick={() => {
                            setShowRejectForm(false);
                            setRejectNotes('');
                          }}
                          disabled={processing}
                          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                        >
                          취소
                        </button>
                        <button
                          onClick={handleReject}
                          disabled={processing || !rejectNotes.trim()}
                          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                        >
                          {processing ? '처리 중...' : '반려 확정'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">퇴거신청 정보를 찾을 수 없습니다.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoveOutRequestModal; 